import React, { useState, useEffect } from "react";
import axios from "axios";
import { TrendingUp, CreditCard, Users, Clock, CheckCircle2, XCircle, AlertCircle, RefreshCw } from "lucide-react";

import adminApi, { ADMIN_API } from "@/pages/admin/adminApi";

const fmt = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("es-AR").format(n || 0);

const StatCard = ({ title, value, sub, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
    <div className="flex items-start justify-between mb-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <p className="text-sm font-medium text-slate-500 mt-1">{title}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const ESTADO_CONFIG = {
  approved:   { label: "Aprobado",    color: "bg-green-100 text-green-700",  icon: CheckCircle2 },
  acreditado: { label: "Acreditado",  color: "bg-blue-100 text-blue-700",    icon: CheckCircle2 },
  pending:    { label: "Pendiente",   color: "bg-yellow-100 text-yellow-700", icon: Clock },
  in_process: { label: "En proceso",  color: "bg-sky-100 text-sky-700",      icon: Clock },
  rejected:   { label: "Rechazado",   color: "bg-red-100 text-red-700",      icon: XCircle },
  rechazado:  { label: "Rechazado",   color: "bg-red-100 text-red-700",      icon: XCircle },
  refunded:   { label: "Devuelto",    color: "bg-purple-100 text-purple-700", icon: AlertCircle },
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [fechaSync, setFechaSync] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    cargarDashboard();
  }, []);

  const cargarDashboard = async () => {
    try {
      const res = await adminApi.get(`${ADMIN_API}/dashboard`);
      setData(res.data);
    } catch (err) {
      setError("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await adminApi.post(`${ADMIN_API}/sync/mp`, {
        fecha_desde: fechaSync,
        fecha_hasta: fechaSync,
      });
      setSyncMsg(`✅ Sincronización completada`);
      cargarDashboard(); // refrescar stats
    } catch (e) {
      setSyncMsg(`❌ ${e.response?.data?.detail || "Error al sincronizar"}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700">{error}</div>
  );

  // Gráfico simple de barras con los últimos 7 días
  const maxTotal = Math.max(...(data.ultimos_7_dias.map(d => d.total) || [1]));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Resumen de recaudación en línea</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Recaudado hoy"
          value={fmt(data.total_hoy)}
          icon={TrendingUp}
          color="bg-green-500"
        />
        <StatCard
          title="Recaudado este mes"
          value={fmt(data.total_mes)}
          sub={`${fmtNum(data.contribuyentes_mes)} contribuyentes`}
          icon={CreditCard}
          color="bg-blue-500"
        />
        <StatCard
          title="Recaudado este año"
          value={fmt(data.total_anio)}
          icon={TrendingUp}
          color="bg-sky-500"
        />
        <StatCard
          title="Pendientes Tesorería"
          value={fmtNum(data.pendientes_cantidad)}
          sub={fmt(data.pendientes_monto)}
          icon={Clock}
          color="bg-amber-500"
        />
      </div>

      {/* Panel de sincronización MP */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <h2 className="font-bold text-slate-700">Sincronizar pagos de MP</h2>
            <p className="text-xs text-slate-400 mt-0.5">Importa todos los pagos (web, POS, QR) de una fecha desde la API de MP</p>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <input
              type="date"
              value={fechaSync}
              onChange={e => setFechaSync(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-xl transition-colors text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </button>
          </div>
          {syncMsg && (
            <p className={`w-full text-sm font-medium ${syncMsg.startsWith("✅") ? "text-green-600" : "text-red-500"}`}>
              {syncMsg}
            </p>
          )}
        </div>
      </div>

      {/* Gráfico + Estados */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Últimos 7 días */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h2 className="font-bold text-slate-700 mb-6">Recaudación — Últimos 7 días</h2>
          {data.ultimos_7_dias.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-8">Sin datos en este período</p>
          ) : (
            <div className="flex items-end gap-3 h-40">
              {data.ultimos_7_dias.map((d, i) => {
                const altura = maxTotal > 0 ? (d.total / maxTotal) * 100 : 0;
                const fecha = new Date(d.fecha + "T00:00:00");
                const label = fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="relative w-full flex flex-col justify-end h-32">
                      {/* Tooltip */}
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        {fmt(d.total)}
                      </div>
                      <div
                        className="w-full bg-blue-500 rounded-t-lg hover:bg-blue-600 transition-colors cursor-default"
                        style={{ height: `${Math.max(altura, 4)}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-400">{label}</span>
                    <span className="text-xs text-slate-500 font-medium">{d.cantidad}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-4 text-center">Las barras indican monto. El número debajo indica cantidad de pagos.</p>
        </div>

        {/* Por estado */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h2 className="font-bold text-slate-700 mb-6">Pagos por estado</h2>
          <div className="space-y-3">
            {data.por_estado.map((e) => {
              const cfg = ESTADO_CONFIG[e.estado] || { label: e.estado, color: "bg-slate-100 text-slate-700", icon: AlertCircle };
              const Ico = cfg.icon;
              return (
                <div key={e.estado} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${cfg.color}`}>
                      <Ico className="w-3 h-3" />
                      {cfg.label}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-700">{fmt(e.total)}</p>
                    <p className="text-xs text-slate-400">{e.cantidad} pagos</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
